import { useState, useMemo, useCallback } from "react";
import { IonPage, IonContent } from "@ionic/react";
import { ChevronDown, ChevronUp, TriangleAlert } from "lucide-react";
import { PageHeader } from "../../components/ui/PageHeader";
import { PrimaryButton } from "../../components/ui/Button";
import { z } from "zod";
import { useStore } from "../../store/useStore";
import { useToast } from "../../hooks/useToast";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

const ZEffortBudget = z.object({
  mon: z.number(),
  tue: z.number(),
  wed: z.number(),
  thu: z.number(),
  fri: z.number(),
  sat: z.number(),
  sun: z.number(),
});
const DAY_LABELS: Record<string, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

const DEFAULT_BUDGET: Record<string, number> = {
  mon: 10, tue: 10, wed: 10, thu: 10, fri: 10, sat: 15, sun: 0,
};

// Weights per preset: [mon, tue, wed, thu, fri, sat, sun]
const PRESETS: { label: string; description: string; weights: number[] }[] = [
  { label: "Even",               description: "Same every day (Sun off)", weights: [1, 1, 1, 1, 1, 1, 0] },
  { label: "Lighter weekends",   description: "More on weekdays",         weights: [2, 2, 2, 2, 2, 1, 0] },
  { label: "Lighter weekdays",   description: "More on Sat & Sun",        weights: [1, 1, 1, 1, 1, 2, 2] },
];

function Stepper({
  value,
  label,
  onChange,
}: {
  value: number;
  label: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-muted-foreground text-xs font-bold">{label}</span>
      <div className="flex flex-col items-center rounded-2xl bg-surface border border-border overflow-hidden">
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="w-11 h-11 flex items-center justify-center text-muted-foreground hover:bg-surface-raised hover:text-foreground transition-colors active:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          aria-label={`Increase ${label}`}
        >
          <ChevronUp className="w-4 h-4" aria-hidden="true" />
        </button>
        <output
          aria-label={`${label} points`}
          className="w-11 h-9 flex items-center justify-center text-foreground text-base font-extrabold tabular-nums"
        >
          {value}
        </output>
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={value === 0}
          className="w-11 h-11 flex items-center justify-center text-muted-foreground hover:bg-surface-raised hover:text-foreground transition-colors active:bg-accent disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          aria-label={`Decrease ${label}`}
        >
          <ChevronDown className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default function EffortSchedulePage() {
  const user = useStore((s) => s.user);
  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const userHouseholds = useStore((s) => s.userHouseholds);
  const tasks = useStore((s) => s.tasks);
  const updateEffortBudget = useStore((s) => s.updateEffortBudget);
  const showToast = useToast((s) => s.showToast);

  const currentUh = userHouseholds.find(
    (uh) => uh.household_uuid === currentHouseholdId && uh.user_uuid === user?.id
  );

  const parsedBudget = ZEffortBudget.safeParse(currentUh?.effort_budget);
  const savedBudget: Record<string, number> = parsedBudget.success ? parsedBudget.data : DEFAULT_BUDGET;
  const [budget, setBudget] = useState<Record<string, number>>({ ...savedBudget });
  const [isSaving, setIsSaving] = useState(false);

  const weeklyRequiredPoints = useMemo(() => {
    return tasks.reduce((total, task) => {
      const freqDays =
        task.frequency_unit === "days" ? task.frequency_number :
        task.frequency_unit === "weeks" ? task.frequency_number * 7 :
        task.frequency_unit === "months" ? task.frequency_number * 30 :
        task.frequency_number * 365;
      return total + (task.points * 7) / freqDays;
    }, 0);
  }, [tasks]);

  const weeklyBudget = useMemo(
    () => DAY_KEYS.reduce((sum, d) => sum + (budget[d] ?? 0), 0),
    [budget]
  );

  const requiredCeil = Math.ceil(weeklyRequiredPoints);
  const isSustainable = weeklyBudget >= weeklyRequiredPoints;
  const deficit = requiredCeil - weeklyBudget;

  // Dirty state: has the user changed anything from the saved version?
  const isDirty = useMemo(
    () => DAY_KEYS.some((d) => (budget[d] ?? 0) !== (savedBudget[d] ?? 0)),
    [budget, savedBudget]
  );

  // Progress bar percentage (capped at 100 for the bar, but label can exceed)
  const coveragePercent = requiredCeil > 0
    ? Math.min(100, Math.round((weeklyBudget / requiredCeil) * 100))
    : 100;

  const progressColor = isSustainable ? "bg-emerald-500" : coveragePercent >= 60 ? "bg-amber-400" : "bg-red-500";

  const applyPreset = useCallback((weights: number[]) => {
    const required = Math.ceil(weeklyRequiredPoints);
    const totalWeight = weights.reduce((s, w) => s + w, 0);
    if (totalWeight === 0) return;

    const newBudget: Record<string, number> = {};
    let assigned = 0;

    DAY_KEYS.forEach((day, i) => {
      const exact = (required * weights[i]) / totalWeight;
      newBudget[day] = Math.floor(exact);
      assigned += newBudget[day];
    });

    let remainder = required - assigned;
    if (remainder > 0) {
      const fractionals = DAY_KEYS
        .map((day, i) => ({
          day,
          frac: ((required * weights[i]) / totalWeight) - Math.floor((required * weights[i]) / totalWeight),
        }))
        .filter((d) => d.frac > 0)
        .sort((a, b) => b.frac - a.frac);

      for (const { day } of fractionals) {
        if (remainder <= 0) break;
        newBudget[day] += 1;
        remainder -= 1;
      }
    }

    setBudget(newBudget);
  }, [weeklyRequiredPoints]);

  const handleChange = (day: string, value: number) => {
    setBudget((prev) => ({ ...prev, [day]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateEffortBudget(budget);
      showToast("Schedule saved", "success");
    } catch {
      showToast("Failed to save schedule", "danger");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <IonPage>
      <PageHeader title="Effort Schedule" backHref="/settings" />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 py-4 pb-28 space-y-4">

          {/* 1. Coverage progress — instant status at a glance */}
          <div className="card-surface p-4">
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-foreground text-2xl font-extrabold tabular-nums">
                {weeklyBudget}{" "}
                <span className="text-sm font-medium text-muted-foreground">of {requiredCeil} pts / week</span>
              </span>
              <span
                className={`text-xs font-bold rounded-md px-2 py-0.5 ${
                  isSustainable
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                }`}
              >
                {isSustainable ? "On track" : `${deficit} pts short`}
              </span>
            </div>
            <div
              className="w-full h-2.5 bg-surface-raised rounded-full overflow-hidden"
              role="progressbar"
              aria-label="Weekly coverage"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={coveragePercent}
            >
              <div
                className={`h-full ${progressColor} rounded-full transition-all duration-300 ease-out`}
                style={{ width: `${coveragePercent}%` }}
              />
            </div>
          </div>

          {/* 2. Day steppers — the core interaction */}
          <div className="card-surface p-4">
            <h2 className="font-bold text-foreground">Daily points</h2>
            <p className="text-muted-foreground text-sm mb-4">
              Points you plan to earn each day.
            </p>
            <div className="flex justify-between gap-1">
              {DAY_KEYS.map((day) => (
                <Stepper
                  key={day}
                  label={DAY_LABELS[day]}
                  value={budget[day] ?? 0}
                  onChange={(v) => handleChange(day, v)}
                />
              ))}
            </div>
          </div>

          {/* 3. Quick presets */}
          <div className="card-surface p-4">
            <h2 className="font-bold text-foreground">Quick presets</h2>
            <p className="text-muted-foreground text-sm mb-3">
              Auto-fills to hit {requiredCeil} pts
            </p>
            <div className="flex flex-col gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => applyPreset(preset.weights)}
                  className="flex items-center justify-between w-full min-h-[48px] bg-surface hover:bg-surface-raised px-4 py-3 rounded-2xl transition-colors active:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="text-foreground text-sm font-semibold">{preset.label}</span>
                  <span className="text-muted-foreground text-xs">{preset.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 4. Sustainability warning — actionable copy */}
          {!isSustainable && (
            <div
              role="status"
              className="flex gap-3 rounded-2xl p-4 bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/25"
            >
              <TriangleAlert className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-300" aria-hidden="true" />
              <div>
                <p className="text-amber-900 dark:text-amber-200 text-sm font-bold mb-0.5">
                  {deficit} points short
                </p>
                <p className="text-amber-800 dark:text-amber-200/80 text-sm">
                  Your budget won't cover all tasks this week. Use a preset above or increase individual days.
                </p>
              </div>
            </div>
          )}

          {/* 5. Save */}
          <PrimaryButton
            onClick={handleSave}
            disabled={!isDirty}
            loading={isSaving}
          >
            {isSaving ? "Saving..." : isDirty ? "Save Schedule" : "No Changes"}
          </PrimaryButton>

        </div>
      </IonContent>
    </IonPage>
  );
}
